import React, { useState, useRef, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import api from '../../../api/axios';

const AgentTestChatScreen = ({ agent, agentName, onBack, isOpen }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const sessionIdRef = useRef('');

  const displayName = agentName || agent?.name || 'AI Assistant';

  // Generate a stable session ID per chat session and fetch greeting from backend
  useEffect(() => {
    if (isOpen) {
      const newSessionId = `test-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      sessionIdRef.current = newSessionId;
      setMessages([]);
      setInputValue('');
      setIsTyping(true);

      // Fetch greeting from backend by sending an init message
      const fetchGreeting = async () => {
        try {
          const { data } = await api.post('/api/test-chat', {
            text: '',
            agent_id: agent?.agent_id || agent?.id,
            session_id: newSessionId,
          });
          if (data?.text?.trim()) {
            setMessages([{
              id: Date.now(),
              role: 'assistant',
              content: data.text.trim(),
              timestamp: new Date(),
            }]);
          }
        } catch (err) {
          console.error('Failed to fetch greeting:', err);
        } finally {
          setIsTyping(false);
        }
      };
      fetchGreeting();
    }
  }, [isOpen, agent]);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const sendMessage = useCallback(async (text) => {
    setIsTyping(true);
    try {
      const { data } = await api.post('/api/test-chat', {
        text,
        agent_id: agent?.agent_id || agent?.id,
        session_id: sessionIdRef.current,
      });
      setMessages(prev => [...prev, {
        id: Date.now(),
        role: 'assistant',
        content: data?.text || 'No response received.',
        timestamp: new Date(),
      }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        id: Date.now(),
        role: 'assistant',
        content: 'Failed to get response. Please try again.',
        timestamp: new Date(),
      }]);
    } finally {
      setIsTyping(false);
    }
  }, [agent]);

  const handleSend = () => {
    const trimmed = inputValue?.trim();
    if (!trimmed || isTyping) return;
    const userMsg = {
      id: Date.now(),
      role: 'user',
      content: trimmed,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMsg]);
    setInputValue('');
    if (textareaRef?.current) {
      textareaRef.current.style.height = 'auto';
    }
    sendMessage(trimmed);
    if (typeof fbq === 'function') fbq('trackCustom', 'agentconfig-chat-message-sent', { content_category: 'AgentConfigPage' });
    window.dataLayer?.push({ event: 'agentconfig_chat_message_sent' });
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSend();
    }
  };

  const handleTextareaChange = (e) => {
    setInputValue(e?.target?.value);
    const ta = textareaRef?.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = Math.min(ta?.scrollHeight, 160) + 'px';
    }
  };

  const formatTime = (date) => {
    return date?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex-shrink-0 bg-card border-b border-border px-4 py-3 flex items-center gap-3 shadow-sm">
        <button
          onClick={onBack}
          className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          aria-label="Go back"
        >
          <Icon name="ArrowLeft" size={18} />
        </button>

        {/* Agent Avatar */}
        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Icon name="Bot" size={18} className="text-primary" />
        </div>

        {/* Agent Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-foreground truncate">{displayName}</span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800 flex-shrink-0">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
              Testing Mode
            </span>
          </div>
          <p className="text-xs text-muted-foreground">AI Assistant</p>
        </div>
      </div>
      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-6">
        {messages?.length === 0 && !isTyping ? (
          /* Empty State */
          (<div className="flex flex-col items-center justify-center h-full text-center px-4 py-16">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
              <Icon name="MessageSquare" size={28} className="text-primary" />
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Start Testing Your AI Assistant
            </h2>
            <p className="text-sm text-muted-foreground max-w-sm leading-relaxed">
              Ask questions just like your customers would and see how your AI Agent responds.
            </p>
            <div className="mt-6 flex flex-wrap gap-2 justify-center">
              {['How can you help me?', 'What are your capabilities?', 'Tell me about your services']?.map((suggestion) => (
                <button
                  key={suggestion}
                  onClick={() => {
                    setInputValue(suggestion);
                    textareaRef?.current?.focus();
                  }}
                  className="px-3 py-1.5 rounded-full text-xs font-medium bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground border border-border transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>)
        ) : (
          <div className="max-w-2xl mx-auto w-full space-y-4">
            {messages?.map((msg) => (
              <div
                key={msg?.id}
                className={`flex items-end gap-2.5 ${msg?.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
              >
                {/* Avatar for AI */}
                {msg?.role === 'assistant' && (
                  <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mb-1">
                    <Icon name="Bot" size={14} className="text-primary" />
                  </div>
                )}

                <div className={`flex flex-col gap-1 max-w-[75%] ${msg?.role === 'user' ? 'items-end' : 'items-start'}`}>
                  <div
                    className={`px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${
                      msg?.role === 'user' ?'bg-primary text-primary-foreground rounded-br-sm' :'bg-muted text-foreground rounded-bl-sm border border-border'
                    }`}
                  >
                    {msg?.content}
                  </div>
                  <span className="text-[11px] text-muted-foreground px-1">
                    {formatTime(msg?.timestamp)}
                  </span>
                </div>
              </div>
            ))}

            {/* Typing Indicator */}
            {isTyping && (
              <div className="flex items-end gap-2.5">
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mb-1">
                  <Icon name="Bot" size={14} className="text-primary" />
                </div>
                <div className="bg-muted border border-border px-4 py-3 rounded-2xl rounded-bl-sm">
                  <style>{`
                    @keyframes typingDot {
                      0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
                      30% { transform: translateY(-6px); opacity: 1; }
                    }
                  `}</style>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-gray-500" style={{ animation: 'typingDot 1.4s ease-in-out infinite', animationDelay: '0ms' }} />
                    <span className="w-2.5 h-2.5 rounded-full bg-gray-500" style={{ animation: 'typingDot 1.4s ease-in-out infinite', animationDelay: '200ms' }} />
                    <span className="w-2.5 h-2.5 rounded-full bg-gray-500" style={{ animation: 'typingDot 1.4s ease-in-out infinite', animationDelay: '400ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>
      {/* Message Input */}
      <div className="flex-shrink-0 bg-card border-t border-border px-4 py-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-end gap-3 bg-muted rounded-2xl border border-border px-4 py-3 focus-within:border-primary/50 focus-within:ring-2 focus-within:ring-primary/10 transition-all">
            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              rows={1}
              className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground resize-none outline-none leading-relaxed max-h-40 scrollbar-hide"
              style={{ minHeight: '24px' }}
            />
            <button
              onClick={handleSend}
              disabled={!inputValue?.trim() || isTyping}
              className="flex-shrink-0 w-8 h-8 rounded-xl bg-primary text-primary-foreground flex items-center justify-center transition-all hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Send message"
            >
              <Icon name="Send" size={14} />
            </button>
          </div>
          <p className="text-[11px] text-muted-foreground text-center mt-2">
            Press <kbd className="px-1 py-0.5 rounded bg-muted border border-border text-[10px] font-mono">Enter</kbd> to send · <kbd className="px-1 py-0.5 rounded bg-muted border border-border text-[10px] font-mono">Shift+Enter</kbd> for new line
          </p>
        </div>
      </div>
    </div>
  );
};

export default AgentTestChatScreen;
